import { fetchWithAuth, flushAccountConfigs, reloadAccountConfigs } from '@blpde/connect';
import { getApiOrigin } from '@/auth';

/**
 * A profile of the account as master lists it (`GET /v1/account/profiles`,
 * backend/master/api/public/account_profiles.go): a named set of the
 * account's settings (office, home office, …). Exactly one profile of an
 * account is its default — every new session starts in it — and `isActive`
 * marks the profile this session works in.
 */
export interface AccountProfile {
  id: number;
  uuid: string;
  name: string;
  accountID: number;
  isDefault: boolean;
  isActive: boolean;
  /** RFC 3339. */
  createdAt?: string;
  updatedAt?: string;
}

/** What `updateAccountProfile` changes; an absent field stays as it is. */
export interface AccountProfileChange {
  name?: string;
  /** `true` makes the profile the account's default (the former default gives the flag up). */
  isDefault?: boolean;
}

/**
 * The error codes master refuses a profile change with — the `code` of its
 * error body.
 */
export const ACCOUNT_PROFILE_ERROR = {
  /** Another profile of the account has the name (trimmed, case-insensitive). */
  nameTaken: 'account_profile_name_taken',
  /** The default profile cannot be deleted or lose its flag; another one becomes the default instead. */
  isDefault: 'account_profile_is_default',
  /** The name is empty. */
  nameRequired: 'missing_field',
  /** The profile is not one of the account's (any more). */
  notFound: 'not_found',
} as const;

/** A refused or failed profile request: the HTTP status and master's error `code`. */
export class AccountProfileRequestError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'AccountProfileRequestError';
    this.status = status;
    this.code = code;
  }
}

const profilesEndpoint = (path = ''): string => `${getApiOrigin()}/v1/account/profiles${path}`;

async function expectOk(response: Response): Promise<Response> {
  if (response.ok) return response;
  let code = '';
  let message = '';
  try {
    const body = (await response.json()) as { code?: unknown; message?: unknown };
    if (typeof body?.code === 'string') code = body.code;
    if (typeof body?.message === 'string') message = body.message;
  } catch {
    /* no JSON body */
  }
  throw new AccountProfileRequestError(response.status, code, message || `HTTP ${response.status}`);
}

function jsonRequest(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

/** The account's profiles, sorted by id. */
export async function fetchAccountProfiles(): Promise<AccountProfile[]> {
  const response = await expectOk(await fetchWithAuth(profilesEndpoint(), { cache: 'no-store' }));
  const body = (await response.json()) as { profiles?: unknown };
  return Array.isArray(body?.profiles) ? (body.profiles as AccountProfile[]) : [];
}

/** Adds a profile; with `isDefault` it becomes the account's default. */
export async function createAccountProfile(
  name: string,
  isDefault = false
): Promise<AccountProfile> {
  const body: { name: string; isDefault?: boolean } = { name };
  if (isDefault) body.isDefault = true;
  const response = await expectOk(
    await fetchWithAuth(profilesEndpoint(), jsonRequest('POST', body))
  );
  return (await response.json()) as AccountProfile;
}

/**
 * Renames a profile and/or makes it the account's default. A new default
 * changes which layers this session's settings come from (the account layer
 * IS the default profile), so the config keys are read again.
 */
export async function updateAccountProfile(
  id: number,
  change: AccountProfileChange
): Promise<AccountProfile> {
  if (change.isDefault) await flushAccountConfigs();
  const response = await expectOk(
    await fetchWithAuth(profilesEndpoint(`/${id}`), jsonRequest('PATCH', change))
  );
  const profile = (await response.json()) as AccountProfile;
  if (change.isDefault) await reloadAccountConfigs();
  return profile;
}

/**
 * Deletes a profile with its settings. Sessions working in it — this one
 * included — move to the account's default profile; the default profile itself
 * is refused (`account_profile_is_default`). The config keys are read again,
 * as this session may have moved.
 */
export async function deleteAccountProfile(id: number): Promise<void> {
  await flushAccountConfigs();
  await expectOk(await fetchWithAuth(profilesEndpoint(`/${id}`), { method: 'DELETE' }));
  await reloadAccountConfigs();
}

/**
 * Lets this session work in the profile `id` (`0`: the account's default
 * profile). Queued setting changes are sent first, so they land in the
 * profile they were made in; afterwards every loaded config key is read
 * again for the new profile (I-2510).
 */
export async function switchSessionProfile(id: number): Promise<void> {
  await flushAccountConfigs();
  await expectOk(
    await fetchWithAuth(`${getApiOrigin()}/v1/session`, jsonRequest('PATCH', { profileID: id }))
  );
  await reloadAccountConfigs();
}
